import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, HostBinding, inject, Inject, OnInit } from '@angular/core';
import { BillingService } from '@features/settings/billing/services/billing.service';
import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';

import {
    AlertTriangle,
    CheckCircle2,
    Info,
    LucideAngularModule,
    X,
    XCircle,
} from 'lucide-angular';

@Component({
    selector: '[app-customized-ngx-toast]',
    imports: [CommonModule, TranslatePipe, LucideAngularModule],
    styles: [
        `
            :host {
                position: relative;
                max-width: 360px;
                background: transparent;
                pointer-events: all;
                cursor: pointer;
                display: block;
                margin-bottom: 0.625rem;
            }
        `,
    ],
    templateUrl: './customized-ngx-toast.component.html',
    animations: [
        trigger('flyInOut', [
            state('inactive', style({ opacity: 0 })),
            transition('inactive => active', [
                style({ transform: 'translateX(20px) scale(0.98)', opacity: 0 }),
                animate(
                    '260ms cubic-bezier(0.16, 1, 0.3, 1)',
                    style({ transform: 'translateX(0) scale(1)', opacity: 1 })
                ),
            ]),
            transition('active => removed', [
                animate(
                    '180ms ease-in',
                    style({ transform: 'translateX(20px) scale(0.98)', opacity: 0 })
                ),
            ]),
        ]),
    ],
    preserveWhitespaces: false,
})
export class CustomizedNgxToastComponent extends Toast implements OnInit {
    private readonly translateS = inject(TranslateService);
    private readonly billingS = inject(BillingService);
    private readonly TRANSLATION_KEYS = TRANSLATION_KEYS.common.error.generic;

    /* Lucide icons */
    protected readonly CheckIcon = CheckCircle2;
    protected readonly InfoIcon = Info;
    protected readonly AlertIcon = AlertTriangle;
    protected readonly XCircleIcon = XCircle;
    protected readonly XIcon = X;

    @HostBinding('style.display')
    get display() {
        if (this.billingS.periodEndExpired() && this.toastPackage.toastType === 'toast-error') {
            return 'none';
        }
        return 'block';
    }

    // used for demo purposes
    undoString = 'undo';

    constructor(
        @Inject(ToastrService) public override toastrService: ToastrService,
        @Inject(ToastPackage) public override toastPackage: ToastPackage
    ) {
        super(toastrService, toastPackage);
    }

    ngOnInit(): void {
        if (this.billingS.periodEndExpired() && this.toastPackage.toastType === 'toast-error') {
            this.remove();
        }
    }

    action(event: Event) {
        event.stopPropagation();
        this.undoString = 'undid';
        this.toastPackage.triggerAction();
        return false;
    }

    closeToast(event: Event) {
        event.stopPropagation();
        this.remove();
    }

    getTitle() {
        if (this.toastPackage.title) return this.toastPackage.title;
        if (this.toastPackage.toastType === 'toast-error') return this.TRANSLATION_KEYS.title;
        return '';
    }
}
